import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Stamp, Printer, ArrowLeft, FileText, Share, Lock, Trash, Download } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Reports() {
  const { id } = useParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (id) {
      api.get(`/writer/rapports/${id}`).then((r) => setCurrent(r.data));
    }
  }, [id]);

  const load = async () => {
    try {
      const r = await api.get("/writer/rapports");
      setReports(r.data);
    } catch (e) { toast.error("Erreur"); }
    finally { setLoading(false); }
  };

  const togglePublic = async () => {
    if (!current) return;
    try {
      if (current.is_public) {
        await api.post(`/writer/rapports/${current.id}/unpublish`);
        toast.success("Pièce dépubliée");
        setCurrent({ ...current, is_public: false });
      } else {
        const r = await api.post(`/writer/rapports/${current.id}/publish`);
        toast.success("Pièce publiée — partage activé");
        setCurrent({ ...current, is_public: true, share_token: r.data.share_token });
      }
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  const copyShareLink = () => {
    if (!current?.share_token) return;
    const url = `${window.location.origin}/share/dvc/${current.share_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien public copié");
  };

  const downloadPdf = async () => {
    if (!current) return;
    try {
      const token = localStorage.getItem("vita_token");
      const r = await fetch(`${BACKEND}/api/writer/rapports/${current.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Erreur téléchargement");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${current.type}-${current.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (e) { toast.error(e.message || "Erreur"); }
  };

  const removeRapport = async () => {
    if (!current || !confirm("Supprimer cet acte ?")) return;
    await api.delete(`/writer/rapports/${current.id}`);
    toast.success("Acte supprimé");
    setCurrent(null);
    load();
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <div className="mb-10 fade-up flex items-end justify-between">
        <div>
          <div className="label-mono">§ 04 · Vita-Writer</div>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight">Actes juridiques</h1>
          <p className="mt-3 text-[var(--muted)] max-w-2xl">Déclarations Vitalistes Citoyennes (DVC) et Requêtes de Reddition de Comptes (RRC) générées par IA juridique.</p>
        </div>
        <Link to="/dashboard" className="label-mono inline-flex items-center gap-2 hover:text-[var(--signal)]" data-testid="reports-back">
          <ArrowLeft size={14} /> Retour
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-black/15 border border-black/15 min-h-[600px]">
        {/* List */}
        <aside className="lg:col-span-4 bg-[var(--paper)] p-6">
          <div className="label-mono mb-3">§ Pièces générées</div>
          {loading ? (
            <div className="label-mono py-6">Chargement...</div>
          ) : reports.length === 0 ? (
            <div className="label-mono py-6 text-[var(--muted)]">Aucun rapport. Lancez une analyse pour générer une DVC ou RRC.</div>
          ) : (
            <ul className="divide-y divide-black/10">
              {reports.map((r) => (
                <li key={r.id} className={`py-3 px-2 hover:bg-[var(--paper-2)] cursor-pointer ${current?.id === r.id ? "bg-[var(--paper-2)]" : ""}`}
                  onClick={() => setCurrent(r)} data-testid={`report-${r.id}`}>
                  <div className="flex items-center gap-3">
                    {r.type === "RRC" ? <Stamp size={18} className="text-[var(--signal)]" /> : <FileText size={18} />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs px-2 py-0.5 border border-black/30">{r.type}</span>
                        <span className="font-mono text-xs text-[var(--muted)]">{fmtDate(r.cree_le)}</span>
                        {r.is_public && <span className="font-mono text-[10px] px-1.5 py-0.5 bg-[var(--signal)] text-[var(--paper)]">PUBLIC</span>}
                      </div>
                      <div className="mt-1 font-serif text-sm truncate">{r.contenu?.split("\n")[0]?.slice(0, 80)}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-6 p-4 border border-black/15 bg-[var(--paper-2)]">
            <div className="label-mono">Conseil</div>
            <p className="mt-2 text-sm text-[var(--muted)]">
              Pour générer un nouveau document, ouvrez une analyse depuis le tableau de bord et cliquez sur « Générer DVC » ou « Générer RRC ».
            </p>
            <Link to="/calculator" className="mt-3 inline-flex label-mono text-[var(--signal)]">+ Nouvelle analyse</Link>
          </div>
        </aside>

        {/* Document Viewer */}
        <main className="lg:col-span-8 bg-[var(--paper)]">
          {!current ? (
            <div className="h-full flex flex-col items-center justify-center p-12 text-center">
              <Stamp size={48} weight="duotone" className="text-[var(--muted)]" />
              <div className="mt-4 font-serif text-2xl text-[var(--muted)]">Sélectionnez un acte</div>
            </div>
          ) : (
            <article className="paper-grain">
              <div className="border-b border-black p-6 flex items-center justify-between gap-4 flex-wrap print:hidden">
                <div>
                  <div className="label-mono">{current.type === "RRC" ? "Requête de Reddition de Comptes" : "Déclaration Vitaliste Citoyenne"}</div>
                  <div className="mt-1 font-mono text-xs text-[var(--muted)]">N° {current.id?.slice(0, 8)} · {fmtDate(current.cree_le)}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={togglePublic} data-testid="report-toggle-public"
                    className={`border px-3 py-2 label-mono flex items-center gap-2 ${current.is_public ? "bg-[var(--signal)] text-[var(--paper)] border-[var(--signal)]" : "border-black hover:bg-black hover:text-[var(--paper)]"}`}>
                    {current.is_public ? <Lock size={14} /> : <Share size={14} />}
                    {current.is_public ? "Dépublier" : "Publier"}
                  </button>
                  {current.is_public && current.share_token && (
                    <button onClick={copyShareLink} data-testid="report-copy-link"
                      className="border border-[var(--signal)] text-[var(--signal)] px-3 py-2 label-mono flex items-center gap-2 hover:bg-[var(--signal)] hover:text-[var(--paper)]">
                      Copier le lien
                    </button>
                  )}
                  <button onClick={downloadPdf} className="border border-black px-3 py-2 hover:bg-black hover:text-[var(--paper)] label-mono flex items-center gap-2" data-testid="report-pdf">
                    <Download size={14} /> PDF
                  </button>
                  <button onClick={() => window.print()} className="border border-black px-3 py-2 hover:bg-black hover:text-[var(--paper)] label-mono flex items-center gap-2" data-testid="report-print">
                    <Printer size={14} /> Imprimer
                  </button>
                  <button onClick={removeRapport} className="border border-black/30 px-3 py-2 text-[var(--muted)] hover:text-[var(--signal)] hover:border-[var(--signal)] label-mono flex items-center gap-2" data-testid="report-delete">
                    <Trash size={14} />
                  </button>
                </div>
              </div>

              {current.is_public && current.share_token && (
                <div className="border-b border-black/15 bg-[var(--paper-2)] p-4 print:hidden">
                  <div className="label-mono text-[var(--signal)]">Acte rendu public</div>
                  <div className="mt-1 font-mono text-xs break-all">{window.location.origin}/share/dvc/{current.share_token}</div>
                </div>
              )}

              <div className="p-8 sm:p-12 max-w-3xl mx-auto">
                <div className="text-center mb-10 stamp-rotate inline-block border-2 border-[var(--signal)] text-[var(--signal)] px-4 py-2 font-mono text-xs">
                  {current.type === "RRC" ? "REDDITION DE COMPTES" : "DÉCLARATION CITOYENNE"}
                </div>
                <pre className="whitespace-pre-wrap font-serif text-base leading-relaxed text-[var(--ink)]" data-testid="report-content">{current.contenu}</pre>
                <div className="mt-12 border-t border-black/15 pt-6 font-mono text-xs text-[var(--muted)] flex items-center justify-between">
                  <div>VITA-LEX ANALYTICA · 2026</div>
                  <div>Pièce {current.id?.slice(0, 8)}</div>
                </div>
              </div>
            </article>
          )}
        </main>
      </div>
    </div>
  );
}
